<template>

    <animated-background>
        <v-dialog persistent transition="scale-transition" max-width="600" v-model="dialog">

            <v-form v-model="formValid" v-if="!success">

                <v-card>
                    <v-toolbar dense flat color="grey darken-4">
                        <v-toolbar-title>New password</v-toolbar-title>
                    </v-toolbar>

                    <v-card-text>

                        <v-alert type="error" class="body-2" v-show="responseError">
                            {{ responseError }}
                        </v-alert>

                        <p>
                            Please enter your email address for verification and your new password. Your password
                            should be at least 7 characters long and contain a combination of upper
                            and lower case letters numbers and special characters like ~%#@*!.
                        </p>

                        <validation-observer ref="validationObserver" v-slot="{ invalid, errors }">

                            <validation-provider
                                mode="lazy"
                                name="email address"
                                rules="required|email|max:100"
                                v-slot="{ errors }">

                                <v-text-field
                                    label="Email address"
                                    v-model="form.email"
                                    required
                                    :error-messages="errors"
                                    prepend-icon="mdi-email-outline"
                                    @blur="validate"
                                ></v-text-field>

                            </validation-provider>

                            <password-field
                                v-model="form.password"
                                label="New password"
                                label-confirm="Confirm password"
                                :error-messages="form.getError('password')"
                                @enter="submitForm"
                                @valid="setPasswordValid"
                            />

                            <v-alert type="error"
                                     v-show="throttleSecondsRemaining>0"
                                     icon="mdi-alert-circle"
                                     class="body-2"
                                     v-text="throttleError"></v-alert>
                        </validation-observer>

                    </v-card-text>

                    <v-card-actions>
                        <v-btn class="ml-2 mb-2"
                               :disabled="submitDisabled"
                               :loading="submitting"
                               color="primary"
                               @click="submitForm">Reset password
                        </v-btn>
                        <v-btn text class="ml-2 mb-2" @click="cancel">Cancel</v-btn>
                    </v-card-actions>
                </v-card>

            </v-form>

            <v-card v-if="success">

                <v-toolbar dense flat color="grey darken-4">
                    <v-toolbar-title>Success!</v-toolbar-title>
                </v-toolbar>

                <v-card-text class="d-flex mt-4">

                    <v-icon class="align-self-center" large>mdi-check</v-icon>
                    <div class="align-self-center ml-6">
                        Password reset successful!
                    </div>

                </v-card-text>

                <v-card-text>
                    <v-btn color="primary" @click="goToLogin">
                        Go to login
                    </v-btn>
                </v-card-text>

            </v-card>

        </v-dialog>
    </animated-background>

</template>

<script>

import PasswordField from '../../../components/UI/PasswordField.vue';
import {fingerprint} from '../../../components/mixins/fingerprintMixin';
import {throttleTimer} from '../../../components/mixins/throttleTimer';
import {emailValidationRules} from '../../../components/mixins/emailValidationRulesMixin';
import Form from '../../../components/forms/Form';
import axios from 'axios';
import AnimatedBackground from "../../../components/UI/AnimatedBackground";

export default {

    mixins: [fingerprint, throttleTimer, emailValidationRules],

    components: {
        AnimatedBackground,
        'password-field': PasswordField
    },

    props: {
        token: {
            required: true,
            type: String,
        }
    },

    data() {
        return {
            form: new Form({
                email: '',
                token: '',
                password: '',
                password_confirmation: '',
            }),
            formValid: false,
            dialog: false,
            submitting: false,
            success: false,
            emailValid: false,
            passwordValid: false,
            throttleTimerRunning: false,
            responseError: null,
        }
    },

    computed: {
        submitDisabled() {
            return !this.emailValid || !this.passwordValid || this.throttleTimerRunning;
        }
    },

    mounted() {

        // If token wasn't passed in on URL
        if (!this.token) {

            this.sendToUrl('/logout');

        } else {

            this.form.token = this.token;

            // Needed for routing back into this component due to the timeout
            setTimeout(() => {
                this.dialog = true;
            }, 200);
        }
    },

    methods: {

        setPasswordValid(valid) {
            this.passwordValid = valid;
        },

        throttleTimerDone() {
            this.throttleTimerRunning = false;
        },

        validate() {
            this.$refs.validationObserver.validate()
                .then(isValid => {
                    this.emailValid = isValid;
                });
        },

        submitForm() {

            this.$refs.validationObserver.validate()
                .then(isValid => {

                    if (isValid) {

                        if (this.form.data.password === this.form.data.password_confirmation) {

                            this.submitting = true;

                            // Laravel standard reset requires this
                            this.form.password_confirmation = this.form.password;

                            axios.post('/password/reset', this.form)
                                .then(response => {
                                    this.success = true;
                                })
                                .catch(error => {

                                    if (error.response.status === 406) {
                                        this.responseError = "Unknown email or password reset link has expired!";
                                    } else if (error.response.status === 422) {
                                        this.responseError = error.response.data.errors[Object.keys(error.response.data.errors)[0]][0];
                                    } else if (error.response.status === 429) {
                                        this.throttleTimerRunning = true;
                                        this.startThrottleTimer(this.throttleTimerDone);
                                    } else {
                                        console.error(error);
                                    }
                                })
                                .finally(() => {
                                    this.submitting = false;
                                });
                        }
                    }
                });
        },

        goToLogin() {
            this.$router.push('/spa/login');
        },

        cancel() {
            this.sendToUrl('/logout');
        }
    }
}

</script>
